import { useQuery } from '@tanstack/react-query';
import { useLocale } from 'next-intl';
import { useMemo } from 'react';

import { getQueryConfig } from '@/lib/tanstackQuery/queryRegistry';
import { MealTranslation } from '@/lib/tanstackQuery/utils';

interface MealTranslationResult {
  name: string;
  description: string | null;
  isTranslated: boolean;
}

/**
 * Hook to get translated meal text based on meal ID and current locale
 * Falls back to original text if translation is not available
 */
export function useMealTranslation(
  mealId: string | undefined,
  originalName: string,
  originalDescription: string | null = null,
  forceLocale?: 'en' | 'pt',
): MealTranslationResult {
  const currentLocale = useLocale() as 'en' | 'pt';
  const locale = forceLocale || currentLocale;

  // Fetch all meal translations
  const { queryKey, queryFn } = getQueryConfig('meal-translations');
  const { data: translations = [] } = useQuery({
    queryKey,
    queryFn,
  });

  const translatedText = useMemo(() => {
    if (!mealId || !translations.length) {
      return {
        name: originalName,
        description: originalDescription,
        isTranslated: false,
      };
    }

    // Find translation for this meal in the desired locale
    const translation = translations.find(
      (t: MealTranslation) => t.meal_id === mealId && t.language_code === locale,
    );

    if (translation) {
      return {
        name: translation.name || originalName,
        description: translation.description || originalDescription,
        isTranslated: true,
      };
    }

    // Fallback to original text
    return {
      name: originalName,
      description: originalDescription,
      isTranslated: false,
    };
  }, [mealId, translations, locale, originalName, originalDescription]);

  return translatedText;
}

/**
 * Simplified hook that just returns the translated name
 */
export function useMealName(
  mealId: string | undefined,
  originalName: string,
  forceLocale?: 'en' | 'pt',
): string {
  const { name } = useMealTranslation(mealId, originalName, null, forceLocale);
  return name;
}

/**
 * Hook to get translations for multiple meals at once
 */
export function useMealTranslations(
  meals: Array<{ id: string; name: string; description?: string | null }>,
  forceLocale?: 'en' | 'pt',
): { [mealId: string]: MealTranslationResult } {
  const currentLocale = useLocale() as 'en' | 'pt';
  const locale = forceLocale || currentLocale;

  // Fetch all meal translations
  const { queryKey, queryFn } = getQueryConfig('meal-translations');
  const { data: translations = [] } = useQuery({
    queryKey,
    queryFn,
  });

  const translatedMeals = useMemo(() => {
    const result: { [mealId: string]: MealTranslationResult } = {};

    meals.forEach((meal) => {
      const translation = translations.find(
        (t: MealTranslation) => t.meal_id === meal.id && t.language_code === locale,
      );

      if (translation) {
        result[meal.id] = {
          name: translation.name || meal.name,
          description: translation.description || meal.description || null,
          isTranslated: true && translation.name !== meal.name,
        };
      } else {
        result[meal.id] = {
          name: meal.name,
          description: meal.description || null,
          isTranslated: false,
        };
      }
    });

    return result;
  }, [meals, translations, locale]);

  return translatedMeals;
}
